<template>
  <base-nav
    v-model="showMenu"
    class="navbar-absolute top-navbar"
    :transparent="true"
  >
    <div slot="brand" class="navbar-wrapper">
      <div class="navbar-minimize d-inline"><sidebar-toggle-button /></div>
      <div
        class="navbar-toggle d-inline"
        :class="{ toggled: $sidebar.showSidebar }"
      >
        <button type="button" class="navbar-toggler menu-button" aria-label="Open menu" @click="toggleSidebar">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
        </button>
      </div>
      <a class="navbar-brand">{{ routeName }}</a>
    </div>

    <!-- Phones: one row. The icons take the place of the theme's kebab menu; the location
         and Sign out live in the sidebar drawer (company and location row, user card). -->
    <template slot="toggle-button">
      <div class="mobile-navbar-actions d-lg-none">
        <a href="#" class="mobile-nav-icon text-white" title="Biku chat" aria-label="Biku chat" @click.prevent="onBikuChatClick">
          <i class="tim-icons icon-chat-33"></i>
        </a>
        <a href="#" class="mobile-nav-icon text-white" title="Saved views" aria-label="Saved views" @click.prevent="openSavedViews">
          <i class="tim-icons icon-book-bookmark"></i>
        </a>
        <system-alerts-bell :groups="groups" :trial-days-remaining="daysRemaining" class="mobile-nav-icon text-white" />
        <announcements-bell class="mobile-nav-icon text-white" />
      </div>
    </template>

    <ul class="navbar-nav row ml-auto justify-content-between" style="width:100%">

      <!-- Desktop: Bell + User + Logout -->
      <div class="col-12 d-none d-lg-block">
        <div class="desktop-nav-actions">
          <el-tooltip content="Biku chat" effect="light" :open-delay="300" placement="bottom">
            <a href="#" class="desktop-nav-icon text-white" aria-label="Biku chat" @click.prevent="onBikuChatClick">
              <i class="tim-icons icon-chat-33"></i>
            </a>
          </el-tooltip>
          <el-tooltip content="Saved views" effect="light" :open-delay="300" placement="bottom">
            <a href="#" class="desktop-nav-icon text-white" @click.prevent="openSavedViews">
              <i class="tim-icons icon-book-bookmark"></i>
            </a>
          </el-tooltip>
          <system-alerts-bell :groups="groups" :trial-days-remaining="daysRemaining" class="desktop-bell text-white mr-3" />
          <el-tooltip content="Notifications" effect="light" :open-delay="300" placement="bottom" :disabled="bellOpen">
            <announcements-bell class="desktop-bell text-white" @toggle="bellOpen = $event" />
          </el-tooltip>
        </div>
      </div>
    </ul>
  </base-nav>
</template>
<script>
import { CollapseTransition } from 'vue2-transitions';
import { BaseNav, Modal } from '@/components';
import SidebarToggleButton from './SidebarToggleButton';
import AnnouncementsBell from '@/components/Announcements/AnnouncementsBell';
import SystemAlertsBell from '@/components/SystemAlertsBell.vue';
import { Auth } from 'aws-amplify'
import { openSavedViews as openSavedViewsDrawer } from '@/composables/useSavedViewsDrawer';
import { toggleBikuChat } from '@/composables/useBikuChat';
import BaseAlert from "@/components/BaseAlert";
import moment from 'moment';
import _ from 'lodash';
import { getMe } from '@/services/groupData';
import {
  state as activeGroupState,
  loadGroupList,
  decideSync,
  replaceActiveGroup,
  refreshActiveGroup,
  getActiveGroup
} from '@/util/activeGroup';

const LOCATION_NOTICE_KEY = 'locationNotice'

export default {
  components: {
    BaseAlert,
    SidebarToggleButton,
    CollapseTransition,
    BaseNav,
    Modal,
    AnnouncementsBell,
    SystemAlertsBell
  },
  computed: {
    routeName() {
      const { meta } = this.$route;
      return this.capitalizeFirstLetter(meta.displayName);
    },
    isRTL() {
      return this.$rtl.isRTL;
    },
    groups() {
      return this.activeGroupState.groups;
    },
  },
  data() {
    return {
      activeNotifications: false,
      showMenu: false,
      searchModalVisible: false,
      searchQuery: '',
      trial_start: '',
      trial_end: '',
      daysRemaining: -1,
      activeGroupState,
      bellOpen: false
    };
  },
  methods: {
    openSavedViews() {
      openSavedViewsDrawer();
    },
    onBikuChatClick() {
      toggleBikuChat();
    },
    getAuthToken() {
      return new Promise(function (resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getGroups() {
      let list
      try {
        list = await loadGroupList()
      } catch (err) {
        if (err.response && err.response.status == 401) this.$router.push('/forbidden')
        return
      }
      const outcome = decideSync(list)
      if (outcome.status === 'none') return
      if (outcome.status === 'replaced') {
        // Reload only after the replacement was written (no reload loop when it cannot
        // load), and carry the notice across the reload so it is actually seen.
        if (await replaceActiveGroup(outcome.newId)) {
          sessionStorage.setItem(LOCATION_NOTICE_KEY, 'Location ' + outcome.oldName + ' is no longer available.')
          window.location.reload()
        } else {
          this.$notify({ type: 'warning', message: 'Location ' + outcome.oldName + ' is no longer available. Pick a location from the menu.' })
        }
        return
      }
      await refreshActiveGroup()
      const active = getActiveGroup() || {}

      // These settings pages keep ?id= on the active location's record, as before.
      // Booking sites and waivers are not corrected here: a location can have several,
      // so the cached id is an arbitrary one (a switch from those editors lands on
      // their lists, see SWITCH_REDIRECTS in activeGroup).
      if(this.$route.path === '/purchases/settings'){
        this.$router.push({
          path: this.$route.path,
          query: {
            id: active.payment_settings_id
          }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err);
          }
        });
      }
      if(this.$route.path === '/games/tracker/settings'){
        this.$router.push({
          path: this.$route.path,
          query: {
            id: active.game_tracker_settings_id
          }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err);
          }
        });
      }
      if(this.$route.path === '/photos/settings'){
        this.$router.push({
          path: this.$route.path,
          query: {
            id: active.photo_settings_id
          }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err);
          }
        });
      }
      if(this.$route.path === '/mail/automated/settings'){
        this.$router.push({
          path: this.$route.path,
          query: {
            id: active.automated_email_settings_id
          }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err);
          }
        });
      }
      if(this.$route.path === '/games/edit'){
        let games = JSON.parse('[' + active.games + ']')
        let game_exists = _.find(games, {id: this.$route.query.id})
        if(!game_exists){
          this.$router.push({path: '/games/settings'});
        }
      }
    },
    navigateTo(location){
      this.$router.push(location)
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    getName(){
      return JSON.parse(localStorage.getItem('profile')).first_name + ' ' + JSON.parse(localStorage.getItem('profile')).last_name
    },
    getImage(){
      return JSON.parse(localStorage.getItem('profile')).img
    },
    toggleNotificationDropDown() {
      this.activeNotifications = !this.activeNotifications;
    },
    closeDropDown() {
      this.activeNotifications = false;
    },
    toggleSidebar() {
      this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
    },
    hideSidebar() {
      this.$sidebar.displaySidebar(false);
    },
    toggleMenu() {
      this.showMenu = !this.showMenu;
    },
    async getProfile(){
      // Cached read shared with the layout, so a hard load makes one users/me request
      getMe()
        .then(data => {
          const trialEndDate = moment(data.company.trial_end, 'YYYY-MM-DD'); // Parse trial end date
          const today = moment(); // Get today's date
          if(trialEndDate.isAfter(today, 'day') || trialEndDate.isSame(today, 'day')){
            let trial = {
              trial_start: data.company.trial_start,
              trial_end: data.company.trial_end
            }
            localStorage.setItem('trial', JSON.stringify(trial))
            this.trial_start = data.company.trial_start.substring(0,10)
            this.trial_end = data.company.trial_end.substring(0,10)

            let today = moment().startOf('day'); // ensures we're comparing from midnight
            let trialEnd = moment(this.trial_end, 'YYYY-MM-DD').startOf('day');

            let daysRemaining = trialEnd.diff(today, 'days'); // include today
            this.daysRemaining = daysRemaining;
          } else {
            this.daysRemaining = -1
            localStorage.removeItem('trial')
          }
        })
    },
  },
  mounted() {
    // a notice saved just before a location reload
    const notice = sessionStorage.getItem(LOCATION_NOTICE_KEY)
    if (notice) {
      sessionStorage.removeItem(LOCATION_NOTICE_KEY)
      this.$notify({ type: 'warning', message: notice })
    }
    //mode
    let docClasses = document.body.classList;
    if (localStorage.getItem('mode') == '0') {
      docClasses.add('white-content');
    } else {
      docClasses.remove('white-content');
    }

    if(!localStorage.getItem('trial')){
      this.getProfile()
    } else {
      let trial = JSON.parse(localStorage.getItem('trial'))
      this.trial_start = new Date(trial.trial_start);
      this.trial_end = new Date(trial.trial_end);
      let today = new Date();
      today.setHours(0, 0, 0, 0);

      let trialEnd = new Date(this.trial_end);
      trialEnd.setHours(0, 0, 0, 0);

      let timeDifference = trialEnd - today;
      this.daysRemaining = (Math.ceil(timeDifference / (1000 * 60 * 60 * 24)) + 1) + 1;
    }
    this.getGroups()
  }
};
</script>
<style scoped>
.top-navbar {
  top: 0px;
}

/* Phone navbar: hamburger, page title and icons on one row */
.mobile-navbar-actions {
  display: flex;
  align-items: center;
  gap: 6px; /* each icon already has a 32px wide tap area */
  flex-shrink: 0;
  margin-left: auto;
}

.mobile-nav-icon {
  font-size: 18px;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 44px; /* finger-sized tap target */
}

.mobile-nav-icon:hover {
  color: #e14eca;
}

@media (max-width: 991.98px) {
  /* a long page title shortens instead of pushing the icons off the row */
  .navbar-wrapper {
    flex: 1;
    min-width: 0;
  }

  .navbar-brand {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* Desktop nav */
.desktop-nav-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 6px;
}


.desktop-bell:hover {
  color: #e14eca !important;
}

.desktop-nav-icon {
  display: inline-flex;
  align-items: center;
  margin-right: 1rem;
}
.desktop-nav-icon i { font-size: 1.1rem; }
.desktop-nav-icon:hover {
  color: #e14eca !important;
}
</style>
